"use client";

import { useBuilder } from "@/context/BuilderContext";
import { formatPrice } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { Check, Crown, Sparkles } from "lucide-react";
import { useBuilderOptions } from "@/hooks/useBuilderOptions";

// Hardcoded fallback — used if DB options haven't loaded yet
const FALLBACK_HAIR_TYPES = [
  { value: "100% Virgin Hair", price: 5000, label: "100% Virgin Hair", description: "The finest quality — completely unprocessed human hair", detail: "Hand-selected, never chemically treated. Cuticles intact and aligned for the most natural look, feel, and longevity. Lasts 3-5+ years with proper care.", icon: Crown, badge: "Premium" },
  { value: "Processed Hair", price: 2800, label: "Processed Hair", description: "Beautiful, natural-looking hair at an accessible price", detail: "Professionally processed for consistency and color. Excellent quality with a natural appearance. Lasts 1-2 years with proper care.", icon: Sparkles, badge: "Value" },
];

export function StepHairType() {
  const { selections, setOption } = useBuilder();
  const selected = selections.hair_type;
  const { options } = useBuilderOptions();

  // Use DB options if available, fall back to hardcoded
  const dbOptions = options.hair_type;
  const HAIR_TYPES = dbOptions && dbOptions.length > 0
    ? dbOptions.map((opt, i) => ({
        value: opt.value,
        price: opt.price,
        label: opt.name,
        description: FALLBACK_HAIR_TYPES[i]?.description || "",
        detail: FALLBACK_HAIR_TYPES[i]?.detail || "",
        icon: i === 0 ? Crown : Sparkles,
        badge: i === 0 ? "Premium" : "Value",
      }))
    : FALLBACK_HAIR_TYPES;

  return (
    <div className="grid gap-4 sm:gap-5">
      {HAIR_TYPES.map((type) => {
        const isSelected = selected === type.value;
        const Icon = type.icon;

        return (
          <button
            key={type.value}
            onClick={() => setOption("hair_type", type.value, type.price)}
            className={cn(
              "group relative overflow-hidden rounded-xl border-2 p-5 sm:p-6 text-start transition-all duration-500 ease-out",
              isSelected
                ? "border-brand-gold bg-brand-gold/5 shadow-md shadow-brand-gold/10"
                : "border-brand-dark/8 hover:border-brand-gold-light hover:shadow-sm"
            )}
          >
            <div className="flex items-start gap-4">
              <div
                className={cn(
                  "flex h-12 w-12 items-center justify-center rounded-lg transition-colors duration-500",
                  isSelected
                    ? "bg-brand-gold/15 text-brand-gold"
                    : "bg-brand-dark/5 text-brand-muted group-hover:text-brand-gold-light"
                )}
              >
                <Icon className="h-6 w-6" strokeWidth={1.5} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3
                    className={cn(
                      "font-display text-lg tracking-tight transition-colors duration-500",
                      isSelected ? "text-brand-dark" : "text-brand-dark/80"
                    )}
                  >
                    {type.label}
                  </h3>
                  <span className={cn(
                    "text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full",
                    type.badge === "Premium"
                      ? "bg-brand-gold/10 text-brand-gold-dark"
                      : "bg-brand-dark/10 text-brand-dark"
                  )}>
                    {type.badge}
                  </span>
                </div>
                <p className="text-sm text-brand-muted leading-relaxed">
                  {type.description}
                </p>
                <p className="mt-2 text-xs text-brand-muted/70 leading-relaxed">
                  {type.detail}
                </p>
              </div>

              <div className="text-end shrink-0">
                <p className="font-display text-lg text-brand-dark">
                  {formatPrice(type.price)}
                </p>
                <p className="text-[11px] text-brand-muted mt-0.5">starting at</p>
              </div>
            </div>

            {/* Selected indicator */}
            {isSelected && (
              <div className="absolute top-3 end-3 flex h-6 w-6 items-center justify-center rounded-full bg-brand-gold text-white animate-scale-in">
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
              </div>
            )}
          </button>
        );
      })}

      {/* Included features note */}
      <div className="mt-2 rounded-lg bg-brand-cream/50 border border-brand-gold/15 px-4 py-3">
        <p className="text-xs font-medium text-brand-dark mb-1">Included with every custom wig:</p>
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-y-1.5 sm:gap-x-4 sm:gap-y-1">
          <span className="text-xs text-brand-dark/70 flex items-center gap-1">
            <Check className="w-3 h-3 text-brand-gold" /> Bleached Knots
          </span>
          <span className="text-xs text-brand-dark/70 flex items-center gap-1">
            <Check className="w-3 h-3 text-brand-gold" /> Baby Hairs
          </span>
          <span className="text-xs text-brand-dark/70 flex items-center gap-1">
            <Check className="w-3 h-3 text-brand-gold" /> Pre-Plucked Hairline
          </span>
        </div>
      </div>
    </div>
  );
}
